/**
 * The outbox: sends made offline wait here, with their photos, and go when the app is back online or next
 * opens (iOS has no Background Sync, docs/52 §5). Each carries its idempotency key, so a retry after a dropped
 * reply never makes two (docs/55 E13).
 */
import { ApiError } from './api.ts';
import { invalidate, online, send } from './data.ts';
import { idb } from './idb.ts';
import { createStore } from './store.ts';
import { uploadBlob, type PhotoHint } from './upload.ts';

export interface OutboxItem {
  id: string;
  /** `photos`: photos added to the library with no signal; `body` carries what they show (kind, serviceId). */
  kind: 'job' | 'request' | 'photos';
  createdAt: number;
  body: Record<string, unknown>;
  /** Photos still to upload: blob keys in the `blobs` store, with any upload id already done. */
  photos: { key: string; contentType: string; uploadId: string | null }[];
  failed: string | null;
}

export const outbox = createStore<OutboxItem[]>([]);

async function reload() {
  const items = await idb.all<OutboxItem>('outbox');
  outbox.set(items.sort((a, b) => a.createdAt - b.createdAt));
}

export async function enqueue(item: Omit<OutboxItem, 'createdAt' | 'failed'>, blobs: Record<string, Blob> = {}): Promise<void> {
  for (const [k, b] of Object.entries(blobs)) await idb.set('blobs', k, b);
  await idb.set('outbox', item.id, { ...item, createdAt: Date.now(), failed: null });
  await reload();
}

let flushing = false;

export async function flush(): Promise<void> {
  if (flushing || !online.get()) return;
  flushing = true;
  try {
    await reload();
    for (const item of outbox.get()) {
      try {
        for (const p of item.photos) {
          if (p.uploadId) continue;
          const blob = await idb.get<Blob>('blobs', p.key);
          if (!blob) continue;
          p.uploadId = await uploadBlob(blob, p.contentType, 'photo', 'job', () => undefined, undefined, item.kind === 'photos' ? (item.body as PhotoHint) : {});
          await idb.set('outbox', item.id, item);
        }
        if (item.kind === 'photos') {
          // The uploads are the whole send: the photos are in the library, being checked.
          invalidate('media', 'onboarding', 'home');
        } else if (item.kind === 'job') {
          const uploadIds = [...((item.body.uploadIds as string[]) ?? []), ...item.photos.map((p) => p.uploadId!).filter(Boolean)];
          await send('jobCreate', { body: { ...(item.body as object), uploadIds } as never });
          invalidate('jobs', 'home');
        } else {
          await send('requestCreate', { body: item.body as never });
          invalidate('inbox', 'requests', 'home');
        }
        for (const p of item.photos) await idb.del('blobs', p.key);
        await idb.del('outbox', item.id);
      } catch (e) {
        if (e instanceof ApiError && e.offline) break;
        await idb.set('outbox', item.id, { ...item, failed: e instanceof ApiError ? e.message : String(e) });
      }
    }
  } finally {
    flushing = false;
    await reload();
  }
}

export async function discard(id: string): Promise<void> {
  const item = (await idb.get<OutboxItem>('outbox', id)) ?? null;
  for (const p of item?.photos ?? []) await idb.del('blobs', p.key);
  await idb.del('outbox', id);
  await reload();
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void flush());
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && void flush());
}
